import React, { useState, useMemo, useRef } from 'react';
import { Search, Filter, Zap, Droplets, Settings, Truck, ArrowLeft, FileText, Home, Mail, X, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import SEO from '../components/SEO';
import { useAnalytics } from '../hooks/useAnalytics';

// Simple UI component replacements
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg shadow-md border border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`p-6 ${className}`}>{children}</div>
);

const CardHeader = ({ children, className = "" }) => (
  <div className={`px-6 py-4 ${className}`}>{children}</div>
);

const CardTitle = ({ children, className = "" }) => (
  <h3 className={`text-lg font-semibold ${className}`}>{children}</h3>
);

const Input = ({ className = "", ...props }) => (
  <input
    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${className}`}
    {...props}
  />
);

const Label = ({ children, className = "", ...props }) => (
  <label className={`block text-sm font-medium text-gray-700 mb-1 ${className}`} {...props}>
    {children}
  </label>
);

const Button = ({ children, className = "", ...props }) => (
  <button
    className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Select = ({ children, className = "", ...props }) => (
  <select
    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${className}`}
    {...props}
  >
    {children}
  </select>
);

const SelectTrigger = ({ children, ...props }) => (
  <Select {...props}>
    {children}
  </Select>
);

const SelectContent = ({ children }) => <>{children}</>;

const SelectItem = ({ value, children }) => (
  <option value={value}>{children}</option>
);

const SelectValue = ({ placeholder }) => (
  <option value="">{placeholder}</option>
);

const MHSHireSelector = () => {
  const analytics = useAnalytics();
  
  const [systemVolume, setSystemVolume] = useState('');
  const [fluidType, setFluidType] = useState('');
  const [powerSupply, setPowerSupply] = useState('');
  const [equipmentType, setEquipmentType] = useState('');
  
  // Modal state
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  
  // Form state
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  
  // reCAPTCHA v2 state
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  // Updated equipment database from hireunits.md
  const hireUnits = [
    {
      id: 'hdu-15-25',
      name: 'HDU 15/25',
      type: 'Fine Filter',
      category: 'HDU',
      systemFitMin: 0,
      systemFitMax: 1000,
      pumpRate: 120,
      dirtHolding: 1.5,
      powerSupply: '240V Single Phase',
      powerDetail: '240V, 0.18kW, 4 pole, 10 amp Single Phase',
      applications: ['Hydraulic oil', 'Gear oil', 'Diesel fuel'],
      dimensions: '500L x 400W x 1230H cm',
      weight: 40,
      page: 7,
      image: '/hire-equipment/hdu-15-25.png'
    },
    {
      id: 'hdu-27-27',
      name: 'HDU 27/27',
      type: 'Fine Filter',
      category: 'HDU',
      systemFitMin: 1001,
      systemFitMax: 2500,
      pumpRate: 374,
      dirtHolding: 6,
      powerSupply: '240V Single Phase',
      powerDetail: '240V, 1Ph, 6 pole, 10 amp Single Phase',
      applications: ['Hydraulic oil', 'Gear oil', 'Diesel fuel'],
      dimensions: '820L x 490W x 1140H cm',
      weight: 75,
      page: 7,
      image: '/hire-equipment/hdu-27-27.png'
    },
    {
      id: 'hdu-27-54',
      name: 'HDU 27/54',
      type: 'Fine Filter',
      category: 'HDU',
      systemFitMin: 2501,
      systemFitMax: 3000,
      pumpRate: 600,
      dirtHolding: 12,
      powerSupply: '415V 3 Phase',
      powerDetail: '415V, 0.37kW, 4 pole, 32 amp 3 phase',
      applications: ['Hydraulic oil', 'Gear oil', 'Diesel fuel'],
      dimensions: '1200L x 650W x 1100H cm',
      weight: 95,
      page: 8,
      image: '/hire-equipment/hdu-27-54.png'
    },
    {
      id: 'hdu-27-54-ocm',
      name: 'HDU 27/54 with OCM',
      type: 'Fine Filter with Monitoring',
      category: 'HDU',
      systemFitMin: 2501,
      systemFitMax: 3000,
      pumpRate: 425,
      dirtHolding: 12,
      powerSupply: '415V 3 Phase',
      powerDetail: '415V, 3Ph, 6 pole, 32 amp 5 pin 3 phase',
      applications: ['Hydraulic oil', 'Gear oil', 'Diesel fuel'],
      dimensions: '1160L x 730W x 1040H cm',
      weight: 95,
      monitoring: 'OCM 15',
      page: 8,
      image: '/hire-equipment/hdu-27-54-ocm.png'
    },
    {
      id: 'hdu-427-54',
      name: 'HDU 427/54',
      type: 'Fine Filter',
      category: 'HDU',
      systemFitMin: 6001,
      systemFitMax: 8000,
      pumpRate: 1260,
      dirtHolding: 48,
      powerSupply: '415V 3 Phase',
      powerDetail: '415V, 0.55kW, 3Ph, 4 pole, 32 amp 4 pin 3 phase',
      applications: ['Hydraulic oil', 'Gear oil', 'Diesel fuel', 'Crushers', 'Fixed plant'],
      dimensions: '1200L x 1200W x 1230H cm',
      weight: 500,
      page: 9,
      image: '/hire-equipment/hdu-427-54.png'
    },
    {
      id: 'hdu-427-108',
      name: 'HDU 427/108',
      type: 'Fine Filter',
      category: 'HDU',
      systemFitMin: 8001,
      systemFitMax: 10000,
      pumpRate: 1100,
      dirtHolding: 96,
      powerSupply: '415V 3 Phase',
      powerDetail: '415V, 3Ph, 6 pole, 32 amp 4 pin 3 phase',
      applications: ['Hydraulic oil', 'Gear oil', 'Diesel fuel', 'Crushers', 'Fixed plant'],
      dimensions: '1200L x 1200W x 1800H cm',
      weight: 500,
      page: 9,
      image: '/hire-equipment/hdu-427-108.png'
    },
    {
      id: 'ptu-27-54',
      name: 'PTU 27/54',
      type: 'Fine Separator',
      category: 'PTU',
      systemFitMin: 2501,
      systemFitMax: 3000,
      pumpRate: 630,
      dirtHolding: 12,
      powerSupply: '415V 3 Phase',
      powerDetail: '415V, 3Ph, 6 pole, 32 amp 4 pin 3 phase',
      applications: ['Hydraulic oil', 'Gear oil', 'Diesel fuel', 'Crushers', 'Fixed plant'],
      dimensions: '1100L x 650W x 1700H cm',
      weight: 120,
      page: 10,
      image: '/hire-equipment/ptu-27-54.png'
    },
    {
      id: 'ptu3-27-108',
      name: 'PTU3 27/108',
      type: 'Fine Separator',
      category: 'PTU',
      systemFitMin: 4001,
      systemFitMax: 6000,
      pumpRate: 1260,
      dirtHolding: 24,
      powerSupply: '415V 3 Phase',
      powerDetail: '415V, 3Ph, 4 pole, 32 amp 4 pin 3 phase',
      applications: ['Hydraulic oil', 'Gear oil', 'Diesel fuel', 'Crushers', 'Fixed plant'],
      dimensions: '1100L x 1100W x 2050H cm',
      weight: 225,
      page: 10,
      image: '/hire-equipment/ptu3-27-108.png'
    },
    {
      id: 'ptu3-2x27-108',
      name: 'PTU3 2 X 27/108',
      type: 'Fine Separator (Dual)',
      category: 'PTU',
      systemFitMin: 6001,
      systemFitMax: 10000,
      pumpRate: 1790,
      dirtHolding: 48,
      powerSupply: '415V 3 Phase',
      powerDetail: '415V, 3Ph, 6 pole, 32 amp 4 pin 3 phase',
      applications: ['Hydraulic oil', 'Gear oil', 'Diesel fuel', 'Crushers', 'Fixed plant'],
      dimensions: '1450L x 800W x 2050H cm',
      weight: 210,
      page: 11,
      image: '/hire-equipment/ptu3-2x27-108.png'
    },
    {
      id: 'd10-combi',
      name: 'D10 COMBI',
      type: 'Desorber/Filter',
      category: 'Desorber',
      systemFitMin: 0,
      systemFitMax: 2500,
      pumpRate: 45,
      dirtHolding: 6,
      powerSupply: '240V Single Phase',
      powerDetail: '240V, 1Ph, 4 pole, 15 amp Single Phase',
      applications: ['Hydraulic oil', 'Gear oil', 'Stern drives', 'Thrusters', 'Fixed plant'],
      dimensions: '700L x 700W x 1800H cm',
      weight: 170,
      viscosityRange: '32 to 150 cSt',
      page: 12,
      image: '/hire-equipment/d10-combi.png'
    },
    {
      id: 'd40-disorber',
      name: 'D40 DISORBER',
      type: 'Desorber',
      category: 'Desorber',
      systemFitMin: 10001,
      systemFitMax: 25000,
      pumpRate: 1520,
      emulsifiedWaterRemoval: '8L/hr',
      powerSupply: '415V 3 Phase',
      powerDetail: '415V, 3Ph, 4 pole, Wilko Series 63 amp 5 pin',
      applications: ['Hydraulic oil', 'Gear oil', 'Stern drives', 'Fixed plant'],
      dimensions: '2400H x 1500L x 1500W cm',
      weight: 1300,
      page: 12,
      image: '/hire-equipment/d40-disorber.png'
    },
    {
      id: 'vru-27-108',
      name: 'VRU 27/108',
      type: 'Varnish Removal Unit',
      category: 'VRU',
      systemFitMin: 15000,
      systemFitMax: 45000,
      pumpRate: 45,
      dirtHolding: 24,
      powerSupply: '400V 3 Phase',
      powerDetail: '400V, 3Ph, 4 pole, 20 amp 5 pin 3 phase',
      applications: ['Hydraulics', 'Steam and gas turbines and compressors'],
      dimensions: '1700W x 750D x 1720H cm',
      weight: 450,
      viscosityMax: '68 CST',
      monitoring: 'OCM 15',
      page: 13,
      image: '/hire-equipment/vru-27-108.png'
    },
    {
      id: 'mobile-flushing',
      name: 'Mobile Flushing Unit',
      type: 'Flushing Unit',
      category: 'Flushing',
      systemFitMin: 0,
      systemFitMax: 1000,
      pumpRate: 700,
      dirtHolding: 24,
      powerSupply: '415V 3 Phase',
      powerDetail: '415V, 0.55kW, 3Ph, 8 pole, 32 amp 4 pin 3 phase',
      applications: ['Hydraulic oils', 'Gear oils', 'Mobile final drives'],
      dimensions: '1370L x 1370W x 1690H cm',
      weight: 470,
      monitoring: 'OCM 15',
      page: 13,
      image: '/hire-equipment/mobile-flushing.png'
    }
  ];

  // Filter units based on criteria
  const filteredUnits = useMemo(() => {
    return hireUnits.filter(unit => {
      // Volume filter
      if (systemVolume) {
        const volume = parseInt(systemVolume);
        if (volume < unit.systemFitMin || volume > unit.systemFitMax) {
          return false;
        }
      }

      // Fluid type filter
      if (fluidType && fluidType !== 'all' && !unit.applications.some(app => 
        app.toLowerCase().includes(fluidType.toLowerCase())
      )) {
        return false;
      }

      // Power supply filter
      if (powerSupply && powerSupply !== 'all' && !unit.powerSupply.toLowerCase().includes(powerSupply.toLowerCase())) {
        return false;
      }

      // Equipment type filter
      if (equipmentType && equipmentType !== 'all' && unit.category !== equipmentType) {
        return false;
      }

      return true;
    });
  }, [systemVolume, fluidType, powerSupply, equipmentType]);

  const getRecommendationLevel = (unit, volume) => {
    if (!volume) return 'suitable';
    const vol = parseInt(volume);
    const range = unit.systemFitMax - unit.systemFitMin;
    const position = (vol - unit.systemFitMin) / range;
    
    if (position <= 0.3) return 'minimum';
    if (position >= 0.8) return 'maximum';
    return 'optimal';
  };

  const getRecommendationColor = (level) => {
    switch (level) {
      case 'minimum': return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'optimal': return 'bg-green-100 border-green-300 text-green-800';
      case 'maximum': return 'bg-orange-100 border-orange-300 text-orange-800';
      default: return 'bg-blue-100 border-blue-300 text-blue-800';
    }
  };

  // Modal handlers
  const handleInquiry = (unit) => {
    setSelectedUnit(unit);
    setShowInquiryModal(true);
    setSubmitMessage('');
    analytics.trackEquipmentInquiry(unit.name, unit.type);
  };

  const handleCloseModal = () => {
    setShowInquiryModal(false);
    setSelectedUnit(null);
    setInquiryForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      message: ''
    });
    setSubmitMessage('');
    setRecaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleFormChange = (field, value) => {
    setInquiryForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const onRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const onRecaptchaError = () => {
    setRecaptchaToken(null);
    setSubmitMessage('reCAPTCHA verification failed. Please try again.');
  };

  const onRecaptchaExpired = () => {
    setRecaptchaToken(null);
    setSubmitMessage('reCAPTCHA verification expired. Please verify again.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUnit) return;

    // Validate reCAPTCHA
    if (!recaptchaToken) {
      setSubmitMessage('Please complete the reCAPTCHA verification.');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch('/api/hire-inquiry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          unit: selectedUnit,
          customer: inquiryForm,
          recaptchaToken: recaptchaToken
        }),
      });

      const result = await response.json();

      if (response.ok) {
        // Track successful hire inquiry submission
        analytics.trackEvent('Hire Inquiry Submitted', {
          unit: selectedUnit.name,
          type: selectedUnit.type,
          success: 'true'
        });
        
        setSubmitMessage('Thank you for your inquiry! We will contact you shortly about the ' + selectedUnit.name + '.');
        setTimeout(() => {
          handleCloseModal();
        }, 3000);
      } else {
        // Track failed hire inquiry submission
        analytics.trackEvent('Hire Inquiry Submitted', {
          unit: selectedUnit.name,
          type: selectedUnit.type,
          success: 'false',
          error: result.error || 'Unknown error'
        });
        
        if (result.error && result.error.includes('reCAPTCHA')) {
          setSubmitMessage('reCAPTCHA verification failed. Please try again.');
          // Reset reCAPTCHA
          if (recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
          setRecaptchaToken(null);
        } else {
          setSubmitMessage('Sorry, there was an error sending your inquiry. Please try again or contact us directly.');
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      
      // Track failed hire inquiry submission
      analytics.trackEvent('Hire Inquiry Submitted', {
        unit: selectedUnit.name,
        type: selectedUnit.type,
        success: 'false',
        error: error.message || 'Network error'
      });
      
      setSubmitMessage('Sorry, there was an error sending your inquiry. Please try again or contact us directly.');
    }

    setIsSubmitting(false);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "CC Jensen Equipment Hire",
    "description": "Professional filtration equipment hire service for Australian mining and hydraulics industries",
    "provider": {
      "@type": "Organization",
      "name": "Mining and Hydraulics",
      "url": "https://yourwebsite.com.au/"
    },
    "category": "Industrial Equipment Rental",
    "offers": {
      "@type": "AggregateOffer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "AUD"
    }
  };

  return (
    <>
      <SEO
        title="Equipment Hire | Mining and Hydraulics"
        description="Hire professional CC Jensen filtration equipment for Australian mining operations. HDU filters, PTU separators, desorbers, and mobile flushing units available for rent."
        keywords="equipment hire, filtration equipment rental, CC Jensen hire, hydraulic equipment hire, mining equipment rental, Australian filtration hire, HDU hire, PTU rental"
        url="https://yourwebsite.com.au/hire"
        type="website"
        structuredData={structuredData}
      />
      <div className="w-full bg-gray-50 min-h-screen">
      {/* Navigation Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 shadow-lg">
        <div className="w-full px-2 md:px-4">
          <div className="flex items-center justify-between py-4">
            {/* Left side - Back button and logos */}
            <div className="flex items-center space-x-4">
              <Link 
                to="/" 
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg border border-white/20 hover:border-white/40 transition-all duration-200 text-sm font-medium backdrop-blur-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Form</span>
              </Link>
              
              <div className="hidden md:flex items-center space-x-4">
                <img 
                  src="/logo.png" 
                  alt="CC Jensen Logo" 
                  className="w-12 h-12 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <div className="text-white">
                  <h1 className="text-lg font-bold">CC Jensen</h1>
                  <p className="text-primary-100 text-xs">Equipment Hire</p>
                </div>
              </div>
            </div>

            {/* Right side - Navigation links */}
            <div className="flex items-center space-x-4">
              <Link 
                to="/" 
                className="flex items-center gap-2 text-white hover:text-primary-100 transition-colors text-sm"
              >
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">System Inquiry</span>
              </Link>
              
              <div className="hidden md:block">
                <img 
                  src="/logo2.png" 
                  alt="CC Jensen Logo 2" 
                  className="w-12 h-12 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 lg:px-8 py-6">
        {/* Header */}
        <Card className="mb-6 bg-gradient-to-r from-yellow-400 to-amber-500 text-black border-none">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <CardTitle className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                <Truck className="h-6 w-6 md:h-8 md:w-8" />
                MHS Hire Unit Selector
              </CardTitle>
              <p className="text-base md:text-lg">Find the right filtration equipment for your system</p>
            </div>
            
            {/* Download Brochure Button */}
            <a
              href="https://miningandhydraulics.com.au/wp-content/uploads/2025/09/MHS-Hire-Range-20062025.pdf"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => analytics.trackBrochureDownload('hire_page')}
              className="flex items-center gap-2 px-6 py-3 bg-black text-yellow-400 rounded-lg hover:bg-gray-800 transition-all duration-200 font-medium text-sm shadow-lg hover:shadow-xl border border-black hover:border-gray-700 whitespace-nowrap self-start"
            >
              <Download className="w-4 h-4" />
              Download Brochure
            </a>
          </div>
        </CardHeader>
      </Card>

      {/* Input Form */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg md:text-xl flex items-center gap-2">
            <Settings className="h-5 w-5" />
            System Requirements
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="system-volume">
                <Droplets className="inline w-4 h-4 mr-1" />
                Fluid Volume (Liters)
              </Label>
              <Input
                id="system-volume"
                type="number"
                value={systemVolume}
                onChange={(e) => {
                  setSystemVolume(e.target.value);
                  if (e.target.value) {
                    analytics.trackEquipmentFilter('volume', e.target.value);
                  }
                }}
                placeholder="e.g., 2500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fluid-type">Fluid Type</Label>
              <Select value={fluidType} onChange={(e) => {
                setFluidType(e.target.value);
                if (e.target.value !== 'all') {
                  analytics.trackEquipmentFilter('fluid_type', e.target.value);
                }
              }} id="fluid-type">
                <SelectValue placeholder="All Types" />
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="hydraulic">Hydraulic Oil</SelectItem>
                <SelectItem value="gear">Gear Oil</SelectItem>
                <SelectItem value="diesel">Diesel Fuel</SelectItem>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="power-supply">
                <Zap className="inline w-4 h-4 mr-1" />
                Power Supply
              </Label>
              <Select value={powerSupply} onChange={(e) => {
                setPowerSupply(e.target.value);
                if (e.target.value !== 'all') {
                  analytics.trackEquipmentFilter('power_supply', e.target.value);
                }
              }} id="power-supply">
                <SelectValue placeholder="All Power Types" />
                <SelectItem value="all">All Power Types</SelectItem>
                <SelectItem value="240v single">240V Single Phase</SelectItem>
                <SelectItem value="415v">415V 3 Phase</SelectItem>
                <SelectItem value="400v">400V 3 Phase</SelectItem>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="equipment-type">
                <Filter className="inline w-4 h-4 mr-1" />
                Equipment Type
              </Label>
              <Select value={equipmentType} onChange={(e) => {
                setEquipmentType(e.target.value);
                if (e.target.value !== 'all') {
                  analytics.trackEquipmentFilter('equipment_type', e.target.value);
                }
              }} id="equipment-type">
                <SelectValue placeholder="All Types" />
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="HDU">Fine Filters (HDU)</SelectItem>
                <SelectItem value="PTU">Fine Separators (PTU)</SelectItem>
                <SelectItem value="Desorber">Desorber/Filters</SelectItem>
                <SelectItem value="VRU">Varnish Removal</SelectItem>
                <SelectItem value="Flushing">Flushing Units</SelectItem>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold flex items-center">
          <Search className="mr-2 h-5 w-5" />
          Recommended Units ({filteredUnits.length} found)
        </h2>
      </div>

      {filteredUnits.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-gray-500 text-lg">No units match your criteria. Please adjust your requirements.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredUnits.map(unit => {
            const recommendationLevel = getRecommendationLevel(unit, systemVolume);
            return (
              <Card key={unit.id} className="overflow-hidden">
                <div className="bg-gray-800 text-white p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-bold">{unit.name}</h3>
                      <p className="text-gray-300">{unit.type}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getRecommendationColor(recommendationLevel)}`}>
                      {recommendationLevel === 'optimal' ? 'Optimal Match' : 
                       recommendationLevel === 'minimum' ? 'Minimum Capacity' :
                       recommendationLevel === 'maximum' ? 'Maximum Capacity' : 'Suitable'}
                    </span>
                  </div>
                </div>

                <CardContent className="p-4">
                  {unit.image && (
                    <div className="mb-4 relative h-48 w-full overflow-hidden rounded-md bg-gray-100 flex items-center justify-center">
                      <img
                        src={unit.image}
                        alt={`${unit.name} equipment`}
                        className="max-h-full max-w-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const parent = e.target.parentElement;
                          if (parent && !parent.querySelector('.fallback-icon')) {
                            parent.classList.add('bg-gray-100', 'flex', 'items-center', 'justify-center');
                            const icon = document.createElement('div');
                            icon.className = 'fallback-icon text-gray-400 text-sm';
                            icon.innerHTML = 'Image not available';
                            parent.appendChild(icon);
                          }
                        }}
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">System Capacity</p>
                      <p className="font-semibold">{unit.systemFitMin === 0 ? 'Up to' : `${unit.systemFitMin.toLocaleString()} -`} {unit.systemFitMax.toLocaleString()}L</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Pump Rate</p>
                      <p className="font-semibold">{unit.pumpRate}L/hr</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Dirt Holding</p>
                      <p className="font-semibold">{unit.dirtHolding || unit.emulsifiedWaterRemoval || 'N/A'} {unit.dirtHolding ? 'kg' : ''}{unit.emulsifiedWaterRemoval ? ' water/hr' : ''}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Weight</p>
                      <p className="font-semibold">{unit.weight} kg</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-1">Power Supply</p>
                    <p className="font-semibold text-sm">{unit.powerDetail}</p>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-1">Applications</p>
                    <div className="flex flex-wrap gap-1">
                      {unit.applications.map((app, idx) => (
                        <span key={idx} className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
                          {app}
                        </span>
                      ))}
                    </div>
                  </div>

                  {unit.monitoring && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-500 mb-1">Monitoring</p>
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">{unit.monitoring}</span>
                    </div>
                  )}

                  <div className="text-xs text-gray-500 mt-4 pt-4 border-t mb-4">
                    <p>Dimensions: {unit.dimensions}</p>
                    <p>Catalog Reference: Page {unit.page}</p>
                    {unit.viscosityRange && <p>Viscosity Range: {unit.viscosityRange}</p>}
                    {unit.viscosityMax && <p>Max Viscosity: {unit.viscosityMax}</p>}
                  </div>
                  
                  {/* Inquiry Button */}
                  <Button
                    onClick={() => handleInquiry(unit)}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                  >
                    <Mail className="w-4 h-4" />
                    Inquire About This Unit
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

        {/* Contact Info */}
        <Card className="mt-8 bg-gray-800 text-white border-none">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-2">Need Help Choosing?</h3>
            <p className="mb-4">Contact MHS for expert advice on your specific requirements.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="font-medium">Phone</p>
                <p>(08) 9249 2511</p>
              </div>
              <div>
                <p className="font-medium">Website</p>
                <p>mhs.com.au</p>
              </div>
              <div>
                <p className="font-medium">Email</p>
                <p>sales@mhs.com.au</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inquiry Modal */}
      {showInquiryModal && selectedUnit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold">{selectedUnit.name}</h3>
                  <p className="text-blue-100">{selectedUnit.type}</p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Unit Summary */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Equipment Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">System Capacity:</span>
                    <span className="ml-2 font-medium">
                      {selectedUnit.systemFitMin === 0 ? 'Up to' : `${selectedUnit.systemFitMin.toLocaleString()} -`} {selectedUnit.systemFitMax.toLocaleString()}L
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Pump Rate:</span>
                    <span className="ml-2 font-medium">{selectedUnit.pumpRate}L/hr</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Power Supply:</span>
                    <span className="ml-2 font-medium">{selectedUnit.powerSupply}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Weight:</span>
                    <span className="ml-2 font-medium">{selectedUnit.weight} kg</span>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="inquiry-name">
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="inquiry-name"
                      type="text"
                      required
                      value={inquiryForm.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="inquiry-company">Company</Label>
                    <Input
                      id="inquiry-company"
                      type="text"
                      value={inquiryForm.company}
                      onChange={(e) => handleFormChange('company', e.target.value)}
                      placeholder="Company name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="inquiry-email">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="inquiry-email"
                      type="email"
                      required
                      value={inquiryForm.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      placeholder="your.email@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="inquiry-phone">Phone</Label>
                    <Input
                      id="inquiry-phone"
                      type="tel"
                      value={inquiryForm.phone}
                      onChange={(e) => handleFormChange('phone', e.target.value)}
                      placeholder="Phone number"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <Label htmlFor="inquiry-message">
                    Message <span className="text-red-500">*</span>
                  </Label>
                  <textarea
                    id="inquiry-message"
                    required
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={inquiryForm.message}
                    onChange={(e) => handleFormChange('message', e.target.value)}
                    placeholder={`I'm interested in hiring the ${selectedUnit.name} for my application. Please provide more details about availability, pricing, and delivery options.`}
                  />
                </div>

                {/* reCAPTCHA */}
                <div className="mb-6 flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6LdJ7corAAAAAHgZYGE2i8XOhgqsTDOyugNd63r9"
                    onChange={onRecaptchaChange}
                    onErrored={onRecaptchaError}
                    onExpired={onRecaptchaExpired}
                  />
                </div>

                {submitMessage && (
                  <div className={`mb-4 p-3 rounded-lg text-sm ${
                    submitMessage.includes('Thank you') 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-red-100 text-red-700 border border-red-200'
                  }`}>
                    {submitMessage}
                  </div>
                )}

                <div className="flex gap-3 justify-end">
                  <Button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-2 bg-gray-300 text-gray-700 hover:bg-gray-400 focus:ring-gray-500"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || !inquiryForm.name || !inquiryForm.email || !inquiryForm.message}
                    className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        Send Inquiry
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default MHSHireSelector;
